import React, { useContext, useEffect, useState } from 'react';
import { StoreContext } from 'pages/_app';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import classNames from 'classnames';
import GoogleAutoComplete from 'components/GoogleAutoComplete';
import QueryString from 'qs';
import { useRouter } from 'next/router';
import { debounce } from 'utils/helpers/restaurants';
import { MapIcon } from 'assets/img';
import Image from 'next/image';
import ConfirmationModal from 'components/ConfirmationModal';

function RestaurantAddressInput({ menusAvailable, setMenusAvailable, restaurantDetail }) {
  const [internalAddress, setInternalAddress] = useState(null);
  const [addressError, setAddressError] = useState(false);
  const [completeAddressError, setCompleteAddressError] = useState(false);
  const store = useContext(StoreContext);
  const userStore = toJS(store?.userStore);
  const { address, completeAddress } = userStore;
  const router = useRouter();
  const [addressValidationAlert, setAddressValidationAlert] = useState(null);
  const [updatedAddress, setUpdatedAddress] = useState('');

  useEffect(() => {
    setInternalAddress({ address, completeAddress });
  }, [address]);

  const handleOnChange = e => {
    if (addressError) setAddressError(false);
    if (completeAddressError) setCompleteAddressError(false);

    setInternalAddress({ address: e?.target?.value });
  };

  const handleOnBlur = debounce(() => {
    if (!internalAddress?.completeAddress?.city) {
      setInternalAddress({ address, completeAddress });
    }
  }, 1000);

  const setInQuery = address => {
    router.replace(
      {
        pathname: router?.pathname,
        query: {
          ...QueryString.parse(router?.query),
          address,
        },
      },
      undefined,
      {
        shallow: true,
      }
    );
  };

  const onClickLeftButtonInAlert = async () => {
    setAddressValidationAlert(null);

    router.replace(
      {
        pathname: '/directory',
        query: {
          ...router.query,
          address: updatedAddress,
        },
      },
      undefined,
      {
        shallow: true,
      }
    );
  };

  const onClickRightButtonInAlert = () => {
    setAddressValidationAlert(null);
  };

  const isValidAddress = async address => {
    const params = {
      restaurant_id: restaurantDetail?.attributes?.restaurant_id,
      order_address: address,
    };

    store.userStore.setLoader(true);
    let response = await store?.restaurantStore?.validateAddress(params);
    store.userStore.setLoader(false);

    return response.valid;
  };

  return (
    <div className="flex w-full md:mr-2 h-12 pl-4 bg-background rounded">
      <Image src={MapIcon} alt="map-icon" className="" />

      <GoogleAutoComplete
        className={classNames(
          'input-light-1 outline-none focus:outline-none address-inputs font-inter-medium my-0 rounded-lg text-md w-full bg-background pl-2',
          {
            'border-attention border': addressError,
          }
        )}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        value={internalAddress?.address}
        onPlaceSelected={place => {
          const _address = place?.address_components?.reduce(
            (seed, { long_name, short_name, types }) => {
              types.forEach(t => {
                if (t == 'administrative_area_level_1') {
                  seed[t] = short_name; // Set short_name for province
                } else {
                  seed[t] = long_name; // Set long_name for other types
                }
              });
              return seed;
            },
            {}
          );

          store.userStore?.getAndSetTimezone(place?.formatted_address);
          setUpdatedAddress(place?.formatted_address);

          const completeAddress = {
            suite: _address?.subpremise,
            street_number: _address?.street_number,
            street_name: _address?.route,
            city: _address?.locality,
            province: _address?.administrative_area_level_1,
            postal_code: _address?.postal_code,
          };

          isValidAddress(place?.formatted_address).then(isValid => {
            if (isValid) {
              setInQuery(place?.formatted_address);

              store.userStore?.setAddress(place?.formatted_address, completeAddress);
              setInternalAddress({ address: place?.formatted_address, completeAddress });
            } else {
              setAddressValidationAlert(true);
            }
          });
        }}
      />

      {addressValidationAlert && (
        <ConfirmationModal
          avoidClose
          visible={addressValidationAlert}
          setModal={setAddressValidationAlert}
          title="Restaurant outside delivery area"
          body="Please adjust address or choose another restaurant."
          rightButtonText="Select another restaurant"
          leftButtonText="Go back"
          onClickRightButton={onClickLeftButtonInAlert}
          onClickLeftButton={onClickRightButtonInAlert}
          roundedBorder
        />
      )}
    </div>
  );
}

export default observer(RestaurantAddressInput);
